/**
 * PWA Utility Functions for Shopify
 * Handles service worker registration, installation prompts, and status updates
 */

const PWAUtil = (function() {
  // Store the install prompt event for later use
  let deferredPrompt;
  let installPromptShown = false;
  
  // Check if PWA is already installed
  const isPWAInstalled = () => {
    // iOS detection
    if (navigator.standalone) return true;
    
    // Android/Desktop detection
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
    
    return false;
  };
  
  // Show a status message to the user
  const showStatus = (message, duration = 3000) => {
    // Create status element if it doesn't exist
    let statusEl = document.querySelector('.pwa-status');
    if (!statusEl) {
      statusEl = document.createElement('div');
      statusEl.className = 'pwa-status';
      document.body.appendChild(statusEl);
    }
    
    // Set message and show
    statusEl.textContent = message;
    statusEl.classList.add('show');
    
    // Hide after duration
    setTimeout(() => {
      statusEl.classList.remove('show');
    }, duration);
  };

  // Initialize PWA features
  const init = () => {
    if ('serviceWorker' in navigator) {
      const SW_TIMEOUT = 5000;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), SW_TIMEOUT);
      
      // Register service worker - using the asset URL provided by Shopify
      const swUrl = document.querySelector('script[src*="service-worker.js"]')?.src ||
                    window.theme?.assets?.serviceWorker ||
                    '/assets/service-worker.js';
      
      navigator.serviceWorker.register(swUrl, {
        updateViaCache: 'none'
      })
      .then(registration => {
        clearTimeout(timeoutId);
        console.log('Service Worker registered with scope:', registration.scope);
        
        // Check for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              showStatus('App updated! Refresh for latest version.', 5000);
            }
          });
        });
        
        // Periodic SW updates
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000); // Check for updates every hour
      })
      .catch(error => {
        console.error('Service Worker registration failed:', error);
        // Log specific error details for debugging
        if (error.name === 'SecurityError') {
          console.warn('Service Worker security error - scope limitations may apply.');
        }
      });
      
      // Handle the beforeinstallprompt event
      window.addEventListener('beforeinstallprompt', (e) => {
        // Prevent the mini-infobar from appearing on mobile
        e.preventDefault();
        // Store the event so it can be triggered later
        deferredPrompt = e;
      });
      
      // Detect when app is installed
      window.addEventListener('appinstalled', () => {
        hideInstallPrompt();
        showStatus('App installed successfully!');
        deferredPrompt = null;
      });
    }
  };
  
  // Public API
  return {
    init,
    showStatus,
    showInstallPrompt,
    isPWAInstalled
  };
})();

// Initialize when the DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', PWAUtil.init);
} else {
  PWAUtil.init();
}

/**
 * This function handles the registration and updating of the service worker
 * @returns {Promise} A promise that resolves when the service worker is registered
 */
function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    // Try to find the correct service worker URL dynamically
    const swUrl = document.querySelector('script[src*="service-worker.js"]')?.src || 
                  window.theme?.assets?.serviceWorker ||
                  '{{ "service-worker.js" | asset_url }}';
    
    return navigator.serviceWorker.register(swUrl)
      .then(registration => {
        console.log('Service Worker registered with scope:', registration.scope);
        
        // Check for updates on page load
        registration.update();
        
        // Set up periodic checks for service worker updates
        setInterval(() => {
          registration.update();
          console.log('Checking for service worker updates...');
        }, 60 * 60 * 1000); // Check every hour
        
        return registration;
      })
      .catch(error => {
        console.error('Service Worker registration failed:', error);
        // Log more details for debugging
        if (error.name === 'SecurityError') {
          console.warn('Service Worker security error - verify the service worker URL and scope.');
        }
      });
  }
  return Promise.resolve(null);
}

/**
 * Listen for service worker update messages and show a notification to the user
 */
function setupUpdateNotifications() {
  if ('serviceWorker' in navigator) {
    // Listen for messages from the Service Worker
    navigator.serviceWorker.addEventListener('message', event => {
      if (event.data && event.data.type === 'SW_UPDATED') {
        showUpdateNotification(event.data.version);
      }
    });
  }
}

/**
 * Display a notification to the user that an update is available
 * @param {string} version The version of the service worker
 */
function showUpdateNotification(version) {
  // Check if we've already shown this notification
  const lastNotified = localStorage.getItem('pwa_update_notified');
  if (lastNotified === version) return;
  
  // Create notification element if it doesn't exist
  let notification = document.querySelector('.pwa-update-notification');
  if (!notification) {
    notification = document.createElement('div');
    notification.className = 'pwa-update-notification';
    notification.innerHTML = `
      <div class="pwa-update-notification__content">
        <p>A new version of this site is available</p>
        <div class="pwa-update-notification__actions">
          <button class="pwa-update-notification__button pwa-update-notification__button--refresh">Refresh Now</button>
          <button class="pwa-update-notification__button pwa-update-notification__button--later">Later</button>
        </div>
      </div>
    `;
    document.body.appendChild(notification);
    
    // Add styles if not already added
    if (!document.querySelector('#pwa-update-styles')) {
      const style = document.createElement('style');
      style.id = 'pwa-update-styles';
      style.textContent = `
        .pwa-update-notification {
          position: fixed;
          bottom: 20px;
          right: 20px;
          background-color: #ffffff;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 9999;
          padding: 16px;
          max-width: 320px;
          transform: translateY(100%);
          opacity: 0;
          transition: transform 0.3s ease, opacity 0.3s ease;
        }
        .pwa-update-notification.show {
          transform: translateY(0);
          opacity: 1;
        }
        .pwa-update-notification__content {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        }
        .pwa-update-notification__content p {
          margin: 0 0 12px;
          font-size: 14px;
        }
        .pwa-update-notification__actions {
          display: flex;
          gap: 8px;
        }
        .pwa-update-notification__button {
          padding: 8px 12px;
          border-radius: 4px;
          border: none;
          font-size: 12px;
          cursor: pointer;
        }
        .pwa-update-notification__button--refresh {
          background-color: #0072e8;
          color: white;
        }
        .pwa-update-notification__button--later {
          background-color: #f1f1f1;
          color: #333333;
        }
      `;
      document.head.appendChild(style);
    }
    
    // Add event listeners
    notification.querySelector('.pwa-update-notification__button--refresh').addEventListener('click', () => {
      window.location.reload();
    });
    
    notification.querySelector('.pwa-update-notification__button--later').addEventListener('click', () => {
      notification.classList.remove('show');
      // Store that we've shown the notification for this version
      localStorage.setItem('pwa_update_notified', version);
    });
  }
  
  // Show the notification
  setTimeout(() => {
    notification.classList.add('show');
  }, 1000);
}

/**
 * Initialize the PWA functionality
 */
function initPWA() {
  registerServiceWorker();
  setupUpdateNotifications();
  
  // Add other PWA initialization code here
  // ... existing initialization code ...
}

// Initialize PWA functionality when the DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPWA);
} else {
  initPWA();
} 